/**
 * Demote raw HTML to Word-expressible markdown before DOCX/PPTX conversion.
 *
 * Why this exists (GitHub issue #21): AI chats emit styled callout boxes as
 * raw `<div style="...">` blocks. Our HTML/preview path renders them
 * faithfully, but Pandoc's docx/pptx writers DROP raw HTML blocks outright —
 * the callout text silently vanishes from the exported file — and the
 * client-side fallback converter leaks the tags as literal text.
 *
 * The transform is deliberately conservative: it only rewrites the small
 * HTML vocabulary AI chats actually produce (div/span/strong/b/em/i/br/a),
 * leaves fenced code blocks untouched, and leaves any other raw HTML alone
 * (it degrades exactly as before). The HTML/PDF/preview paths never call
 * this — styled callouts stay styled where the target can express them.
 *
 * Raw HTML tables are the exception to "rewrite": they are left whole for
 * Pandoc's own HTML reader (HTML_TABLE_FILTER in lib/server/pandoc-convert.ts),
 * which turns them into real tables. See splitHtmlTables.
 */

/**
 * Cut `text` into prose and raw HTML `<table>…</table>` blocks (a table
 * starts a line; nested tables stay inside their outer table). Each table
 * comes back with its blank lines removed and blank lines around it: a
 * CommonMark HTML block ends at the first blank line, and a table split
 * there would reach Pandoc as fragments it cannot read as a table. Reported
 * 2026-10-03: tables pasted from Word arrive as raw HTML, and the Word
 * export dropped every one of them.
 */
export function splitHtmlTables(text: string): { table: boolean; text: string }[] {
  const parts: { table: boolean; text: string }[] = [];
  const start = /^[ \t]*<table[\s>]/gim;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = start.exec(text))) {
    const tag = /<(\/?)table\b[^>]*>/gi;
    tag.lastIndex = m.index;
    let depth = 0;
    let end = -1;
    let t: RegExpExecArray | null;
    while ((t = tag.exec(text))) {
      depth += t[1] ? -1 : 1;
      if (depth === 0) {
        end = tag.lastIndex;
        break;
      }
    }
    if (end === -1) break; // never closed: leave the rest as it is
    parts.push({ table: false, text: text.slice(last, m.index) });
    parts.push({ table: true, text: text.slice(m.index, end).replace(/\n[ \t]*(?=\n)/g, "").replace(/\n+/g, "\n") });
    last = end;
    start.lastIndex = end;
  }
  parts.push({ table: false, text: text.slice(last) });
  return parts;
}

const attrOf = (tag: string, name: string) =>
  new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s>]+))`, "i").exec(tag)?.slice(1).find((v) => v !== undefined);

/**
 * `<img src alt>` → `![alt](src)`. OCR Markdown wraps every figure as
 * `<div style="text-align: center;"><img …></div>`, and the Word and slide
 * writers drop raw HTML, so the figure vanished (issue #45). By now `src` is
 * a file the server prepared, or the tag was already reduced to its alt text
 * (lib/server/pandoc-images.ts).
 */
function demoteImages(text: string): string {
  return text.replace(/<img\b[^>]*>/gi, (tag) => {
    const src = attrOf(tag, "src");
    const alt = (attrOf(tag, "alt") ?? "").replace(/[[\]]/g, "");
    return src ? `![${alt}](${src})` : alt;
  });
}

/** Convert the inline-HTML vocabulary to markdown equivalents. */
function demoteInline(text: string): string {
  return (
    demoteImages(text)
      .replace(/<(?:strong|b)>([\s\S]*?)<\/(?:strong|b)>/gi, "**$1**")
      .replace(/<(?:em|i)>([\s\S]*?)<\/(?:em|i)>/gi, "*$1*")
      .replace(/<a\s+[^>]*href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, "[$2]($1)")
      // Anchors without href (id/name markers) — keep the inner text only.
      .replace(/<a\s+[^>]*>([\s\S]*?)<\/a>/gi, "$1")
      // <br> INSIDE a pipe-table row must become a space, not a newline — a
      // newline splits the row and destroys the table. GFM has no other way
      // to break a line inside a cell, and <br> in cells is exactly how AI
      // chats keep long two-column tables valid (issue #28: a 213-row
      // bilingual table arrived in Word as 13 rows because every <br> in a
      // cell had cut its row in half). A newline stays correct everywhere
      // else, so demote per line: rows get spaces, prose gets breaks.
      .split("\n")
      .map((line) =>
        line.replace(/<br\s*\/?>/gi, line.trimStart().startsWith("|") ? " " : "\n"),
      )
      .join("\n")
      // Unwrap spans (styling can't survive DOCX anyway; content must).
      .replace(/<\/?span[^>]*>/gi, "")
  );
}

/**
 * Rewrite `<div …>…</div>` blocks as blockquotes and demote inline HTML,
 * skipping fenced code blocks. Nested divs are unwrapped from the inside out.
 */
export function demoteRawHtmlForWord(markdown: string): string {
  // Split out fenced code so we never rewrite documentation-of-HTML.
  const segments = markdown.split(/(^(?:```|~~~)[\s\S]*?^(?:```|~~~)\s*$)/m);
  return segments
    .map((segment, i) => {
      const isFence = i % 2 === 1;
      if (isFence) return segment;
      return splitHtmlTables(segment)
        .map((part) => (part.table ? `\n\n${part.text.trim()}\n\n` : demoteSegment(part.text)))
        .join("");
    })
    .join("");
}

/** Prose between tables: callout divs → blockquotes, inline HTML → markdown. */
function demoteSegment(segment: string): string {
  let out = segment;
  // Innermost-first so nested divs unwrap cleanly.
  const divRe = /<div[^>]*>([^<]*(?:<(?!\/?div)[^<]*)*)<\/div>/gi;
  let prev = "";
  while (prev !== out) {
    prev = out;
    out = out.replace(divRe, (_m, inner: string) => {
      const content = demoteInline(inner).trim();
      if (!content) return "";
      // A div that only centres a figure is a picture, not a callout quote.
      if (/^(?:!\[[^\]]*\]\([^)]*\)\s*)+$/.test(content)) return `\n\n${content}\n\n`;
      const quoted = content
        .split("\n")
        .map((l) => (l.trim() ? `> ${l.trim()}` : ">"))
        .join("\n");
      return `\n\n${quoted}\n\n`;
    });
  }
  return demoteInline(out);
}
